import React, { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navbar } from './Navbar';
import { SparkleIcon, FilmSlateIcon, ArrowRightIcon, CheckCircleIcon, StackIcon, MagicWandIcon, MusicNoteIcon, VideoIcon, UserIcon, SpeakerHighIcon, SpeakerXIcon, ArrowLineRightIcon } from '@phosphor-icons/react';

// --- FOOTER COMPONENT ---
const LandingFooter = () => (
  <footer className="bg-onyx border-t border-white/10 py-20 px-6">
    <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-10">
      <div>
        <h3 className="text-2xl font-bold text-white mb-4">EZ Art</h3>
        <p className="text-zinc-500 max-w-xs">The future of storytelling is here. Start creating your masterpiece today.</p>
      </div>
      <div className="flex gap-10 text-zinc-500">
        <div className="flex flex-col gap-4">
          <span className="text-white font-bold">Product</span>
          <a href="#" className="hover:text-white transition-colors">Features</a>
          <a href="#" className="hover:text-white transition-colors">Pricing</a>
        </div>
        <div className="flex flex-col gap-4">
          <span className="text-white font-bold">Company</span>
          <a href="#" className="hover:text-white transition-colors">About</a>
          <a href="#" className="hover:text-white transition-colors">Careers</a>
        </div>
      </div>
    </div>
  </footer>
);

// --- DATA ---

const workflowSteps = [
  {
    id: 1,
    step: "01",
    icon: FilmSlateIcon,
    color: "indigo",
    title: "AI Storyboard",
    desc: "Upload your script or concept. Our engine breaks it down into scenes, generates shot lists, and visualizes the storyboard instantly.",
    features: ["Auto-Scene Detection", "Shot Visualization"],
    img: "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?auto=format&fit=crop&q=80&w=1200"
  },
  {
    id: 2,
    step: "02",
    icon: UserIcon,
    color: "purple",
    title: "Character Lock",
    desc: "Create consistent characters that look the same in every shot. Change angles, lighting, and expressions without losing their identity.",
    features: ["Face Identity Preservation", "Outfit Consistency"],
    img: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: 3,
    step: "03",
    icon: VideoIcon,
    color: "emerald",
    title: "Direct the Motion",
    desc: "Take control of the camera. Define pan, tilt, and zoom movements. Use the motion brush to animate specific elements in your scene.",
    features: ["Camera Keyframes", "Motion Brush"],
    img: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=1200"
  }
];

const FeatureCard = ({ title, desc, icon: Icon, img }) => (
  <div className="min-w-[320px] md:min-w-[400px] aspect-[3/4] bg-onyx border border-white/10 rounded-3xl overflow-hidden relative group snap-center cursor-pointer hover:border-white/30 transition-all shadow-xl">
    <div className="absolute inset-0">
      <img src={img} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" alt={title} />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
    </div>
    <div className="absolute bottom-0 left-0 p-10 w-full translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
      <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center mb-6 border border-white/10 group-hover:bg-white group-hover:text-black transition-colors">
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="text-3xl font-heading font-bold text-white mb-3">{title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{desc}</p>
    </div>
  </div>
);

// --- MAIN COMPONENT ---

export const LandingPage: React.FC = () => {
  const { openAuthModal } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  const toggleSound = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    // CRITICAL FIX: Removed overflow-x-hidden from the root div. 
    // Stickiness requires ancestors to allow overflow. We handle horizontal overflow on specific sections or body.
    <div className="bg-carbon min-h-screen text-white w-full font-sans selection:bg-indigo-500/30">
      <Navbar />

      {/* GLOBAL STYLES */}
      <style>{`
        @keyframes scroll {
            from { transform: translateX(0); }
            to { transform: translateX(-100%); }
        }
        .animate-marquee {
            animation: scroll 40s linear infinite;
        }
        .animate-marquee:hover {
            animation-play-state: paused;
        }
        .hover-underline-animation span {
            background-image: linear-gradient(transparent calc(100% - 1px), white 1px);
            background-size: 0 100%;
            background-repeat: no-repeat;
            transition: background-size 0.4s ease;
        }
        .hover-underline-animation:hover span {
            background-size: 100% 100%;
        }
        .scrollbar-hide::-webkit-scrollbar {
            display: none;
        }
        .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
      `}</style>

      {/* --- HERO SECTION --- */}
      <section className="relative w-full h-[100svh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-60 scale-105"
            poster="https://images.unsplash.com/photo-1618172193763-c511deb635ca?auto=format&fit=crop&q=80&w=2000"
          >
            <source src="https://cdn.coverr.co/videos/coverr-cloudy-mountains-time-lapse-5264/1080p.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/40 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-900/10 via-transparent to-carbon" />
        </div>

        <button
          onClick={toggleSound}
          className="absolute bottom-10 right-10 z-30 p-3 rounded-full bg-onyx/20 backdrop-blur-xl border border-white/10 text-white hover:bg-white/10 transition-all group"
        >
          {isMuted ? <SpeakerXIcon className="w-5 h-5" /> : <SpeakerHighIcon className="w-5 h-5" />}
        </button>

        <div className="relative z-10 max-w-5xl mx-auto text-center flex flex-col items-center px-6 mt-[-5vh]">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest font-bold text-indigo-300 mb-8 backdrop-blur-md animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <SparkleIcon className="w-3 h-3" />
            <span>The Studio for Everyone</span>
          </div>

          <h1 className="text-6xl md:text-[7rem] lg:text-[8rem] font-heading font-bold tracking-tighter text-white mb-6 leading-[0.9] animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-150 drop-shadow-2xl">
            From Script <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-zinc-200 to-zinc-500">To Screen.</span>
          </h1>

          <p className="text-lg md:text-xl text-zinc-300 max-w-xl mx-auto mb-10 leading-relaxed font-light animate-in fade-in slide-in-from-bottom-10 duration-1000 delay-300 text-balance">
            The complete AI workflow. Turn your text into cast, dialogue, and video in one timeline.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4 animate-in fade-in zoom-in-95 duration-1000 delay-500">
            <button
              onClick={openAuthModal}
              className="h-14 px-8 rounded-full bg-white text-black font-bold text-lg hover:bg-zinc-200 transition-all flex items-center gap-2 shadow-[0_0_40px_rgba(255,255,255,0.15)] hover:shadow-[0_0_60px_rgba(255,255,255,0.3)] active:scale-95 transform hover:-translate-y-1"
            >
              Start Creating Free
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* --- INFINITE MARQUEE --- */}
      <div className="w-full overflow-hidden border-y border-white/5 bg-onyx/50 backdrop-blur-sm py-8 relative z-20">
        <div className="flex w-max animate-marquee">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-20 items-center px-10 text-zinc-500 font-bold text-sm uppercase tracking-[0.2em]">
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">Netflix</span>
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">Pixar</span>
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">A24</span>
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">Sony Pictures</span>
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">Warner Bros</span>
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">Universal</span>
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">Lionsgate</span>
              <span className="hover:text-white transition-colors cursor-default whitespace-nowrap">Paramount</span>
            </div>
          ))}
        </div>
      </div>

      {/* --- STICKY STACKING WORKFLOW (FIXED) --- */}
      <section className="bg-carbon relative px-4 sm:px-6 py-40">
        <div className="max-w-[1400px] mx-auto">

          <div className="mb-24 md:mb-40 text-center max-w-3xl mx-auto">
            <h2 className="text-5xl md:text-7xl font-heading font-bold text-white mb-6">A complete workflow.</h2>
            <p className="text-xl text-zinc-400">
              Don't just generate clips. Build entire stories with a professional suite of tools designed for consistency and control.
            </p>
          </div>

          {/* STICKY LOGIC:
                  1. The parent container must NOT be overflow-hidden.
                  2. We use 'sticky' on the children.
                  3. We stagger the 'top' value so they stack (e.g. top-24, top-32).
                  4. We add marginBottom to create scroll distance between them.
              */}
          <div className="relative">
            {workflowSteps.map((step, index) => (
              <div
                key={step.id}
                className="sticky transition-all duration-700 ease-out"
                style={{
                  // The 'top' value determines where it sticks. We increase it slightly for each card to create the 'stack' effect.
                  top: '15vh',

                  // Z-Index ensures the next card slides OVER the previous one
                  zIndex: index + 1,

                  // This margin creates the 'scroll distance' required to reach the next card.
                  marginBottom: '10vh',

                  // Subtle scale effect for depth when stacked
                  transformOrigin: 'top center'
                }}
              >
                <div className="w-full min-h-[60vh] md:h-[70vh] bg-onyx border border-white/10 rounded-[32px] overflow-hidden flex flex-col md:flex-row shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.8)] backdrop-blur-sm">

                  {/* Text Content */}
                  <div className="flex-1 p-8 md:p-14 flex flex-col justify-center border-b md:border-b-0 md:border-r border-white/5 bg-onyx">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white/5 border border-white/10 text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-8 w-max">
                      <step.icon className="w-3 h-3" />
                      Step {step.step}
                    </div>
                    <h3 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 hover-underline-animation cursor-default">
                      <span>{step.title}</span>
                    </h3>
                    <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-md">
                      {step.desc}
                    </p>
                    <div className="flex flex-col gap-4">
                      {step.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors cursor-default">
                          <div className={`w-8 h-8 rounded-full bg-${step.color}-500/20 flex items-center justify-center text-${step.color}-400 font-bold text-xs`}>
                            {String.fromCharCode(65 + i)}
                          </div>
                          <span className="text-sm font-medium text-zinc-200">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Image Content */}
                  <div className="flex-[1.5] relative bg-carbon min-h-[300px] md:min-h-auto group overflow-hidden">
                    <img
                      src={step.img}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700 ease-in-out"
                      alt={step.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-l from-transparent via-onyx/50 to-onyx" />

                    {/* Decorative Grid */}
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_70%)] pointer-events-none" />
                  </div>
                </div>
              </div>
            ))}
            <div className="h-[150vh]" />
          </div>
        </div>
      </section>

      {/* --- HORIZONTAL FEATURE REEL --- */}
      <section className="py-40 bg-carbon border-t border-white/5 overflow-hidden">
        <div className="max-w-[1800px] mx-auto px-6 mb-16 flex flex-col md:flex-row justify-between items-end gap-6">
          <div>
            <h2 className="text-3xl md:text-6xl font-heading font-bold text-white mb-6">Discover Features</h2>
            <p className="text-zinc-400 text-lg">Tools designed for the modern creative workflow.</p>
          </div>
          <div className="hidden md:flex gap-2 items-center">
            <div className="text-sm text-zinc-500 font-medium">Scroll to explore</div>
            <ArrowLineRightIcon className="w-4 h-4 text-zinc-500 animate-pulse" />
          </div>
        </div>

        <div className="flex gap-8 overflow-x-auto pb-16 px-6 snap-x snap-mandatory scrollbar-hide">
          <FeatureCard
            title="AI Video Generator"
            desc="Text to video in seconds with Nano-Banana & Flux models."
            icon={VideoIcon}
            img="https://images.unsplash.com/photo-1626544827763-d516dce335ca?auto=format&fit=crop&q=80&w=800"
          />
          <FeatureCard
            title="Character Lab"
            desc="Create and store consistent character profiles."
            icon={UserIcon}
            img="https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&q=80&w=800"
          />
          <FeatureCard
            title="Lip Sync"
            desc="Automated dialogue matching for any character."
            icon={MusicNoteIcon}
            img="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&q=80&w=800"
          />
          <FeatureCard
            title="World Building"
            desc="Generate 360-degree environments for your scenes."
            icon={StackIcon}
            img="https://images.unsplash.com/photo-1578328819058-b69f3a3b0f6b?auto=format&fit=crop&q=80&w=800"
          />
          <FeatureCard
            title="Storyboarding"
            desc="Visualize your script shot-by-shot before rendering."
            icon={FilmSlateIcon}
            img="https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&q=80&w=800"
          />
        </div>
      </section>

      {/* --- CTA FOOTER --- */}
      <section className="py-40 px-6 border-t border-white/5 relative overflow-hidden flex flex-col items-center justify-center bg-onyx">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-indigo-900/10 pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-5xl md:text-8xl font-heading font-bold text-white mb-10 tracking-tight">
            Ready to create?
          </h2>
          <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto">
            Join thousands of creators turning their stories into reality with EZ Art Studio.
          </p>
          <button
            onClick={openAuthModal}
            className="h-16 px-12 rounded-full bg-white text-black font-bold text-xl hover:bg-zinc-200 transition-all shadow-[0_0_50px_rgba(255,255,255,0.2)] hover:shadow-[0_0_80px_rgba(255,255,255,0.4)] active:scale-95 transform hover:-translate-y-1"
          >
            Start Creating Free
          </button>
          <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-8 text-sm text-zinc-500 font-medium uppercase tracking-widest">
            <span className="flex items-center gap-2"><CheckCircleIcon className="w-4 h-4 text-emerald-500" /> No credit card required</span>
            <span className="flex items-center gap-2"><CheckCircleIcon className="w-4 h-4 text-emerald-500" /> Free tier available</span>
            <span className="flex items-center gap-2"><CheckCircleIcon className="w-4 h-4 text-emerald-500" /> Commercial Usage</span>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};